import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Clock, ShieldCheck, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Canonical (English) service values are what get stored and emailed to the firm.
const SERVICE_VALUES = translations.en.booking.options;
const POINT_ICONS = [Clock, ShieldCheck, CheckCircle2];

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export default function BookingSection() {
  const { t, lang } = useLang();
  const b = t.booking;
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = b.errName;
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = b.errEmail;
    if (!form.phone.trim() || form.phone.trim().length < 6) e.phone = b.errPhone;
    if (!form.service) e.service = b.errService;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, lang });
      setSubmitted(true);
      setForm(initial);
      toast.success(b.toastOk, { description: b.toastOkDesc });
    } catch (err) {
      toast.error(b.toastErr, { description: err?.response?.data?.detail || b.toastErrDesc });
    } finally {
      setLoading(false);
    }
  };

  const labelCls = "text-xs uppercase tracking-[0.18em] text-ink-500 font-semibold";
  const inputCls = "mt-2 h-12 bg-white border-ink-900/12 text-ink-900 rounded-sm placeholder:text-ink-500/50 focus:border-brand-500 focus:ring-1 focus:ring-brand-500";

  return (
    <section id="book" data-testid="booking-section" className="section-y bg-white relative">
      <div className="container-x grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="hairline" />
            <span className="text-overline">{b.overline}</span>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-ink-900 tracking-tight leading-tight">
            {b.h2a}{" "}
            <span className="text-brand-500">{b.h2accent}</span>{b.h2b}
          </h2>
          <p className="mt-6 text-base text-ink-500 leading-relaxed">{b.sub}</p>

          <ul className="mt-10 space-y-5">
            {b.points.map((text, i) => {
              const Icon = POINT_ICONS[i];
              return (
                <li key={i} className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-brand-500 mt-0.5" strokeWidth={1.8} />
                  <span className="text-sm text-ink-800 font-medium">{text}</span>
                </li>
              );
            })}
          </ul>

          <div className="mt-12 pt-8 border-t border-ink-900/8">
            <p className="text-overline mb-4">{b.prefer}</p>
            <a
              href="tel:+61433022898"
              data-testid="booking-phone-link"
              className="font-display font-bold text-2xl text-ink-900 hover:text-brand-600 transition-colors"
            >
              +61 433 022 898
            </a>
            <p className="text-sm text-ink-500 mt-2">{b.hours}</p>
            {lang === "zh" && (
              <div data-testid="booking-wechat" className="mt-6 flex items-center gap-4">
                <img src="/wechat-qr.png" alt="微信二维码" className="w-24 h-24 border border-ink-900/10 bg-white p-1" loading="lazy" />
                <div className="text-sm text-ink-800 font-medium leading-snug">{b.wechat}</div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative">
            <div className="absolute -inset-3 border-2 border-brand-500 hidden sm:block" style={{ transform: "translate(12px,12px)", zIndex: 0 }} />
            <form
              data-testid="booking-form"
              onSubmit={submit}
              noValidate
              className="relative bg-white border border-ink-900/8 p-8 sm:p-10 shadow-lg"
            >
              {submitted ? (
                <div data-testid="booking-success" className="text-center py-12">
                  <div className="w-14 h-14 mx-auto grid place-items-center bg-brand-50 text-brand-600 rounded-full">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h3 className="mt-6 font-display font-bold text-2xl text-ink-900">{b.successTitle}</h3>
                  <p className="mt-3 text-ink-500 max-w-md mx-auto">{b.successBody}</p>
                  <Button
                    data-testid="booking-submit-another"
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="mt-8 bg-white border-ink-900/15 text-ink-900 hover:bg-cream-50 hover:text-brand-600 hover:border-brand-500 rounded-sm"
                  >
                    {b.another}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label={b.name} id="name" testId="booking-input-name" value={form.name} onChange={set("name")} error={errors.name} placeholder="Jane Doe" labelCls={labelCls} inputCls={inputCls} />
                    <Field label={b.email} id="email" type="email" testId="booking-input-email" value={form.email} onChange={set("email")} error={errors.email} placeholder="jane@example.com" labelCls={labelCls} inputCls={inputCls} />
                    <Field label={b.phone} id="phone" type="tel" testId="booking-input-phone" value={form.phone} onChange={set("phone")} error={errors.phone} placeholder="+61 4XX XXX XXX" labelCls={labelCls} inputCls={inputCls} />
                    <div>
                      <Label htmlFor="service" className={labelCls}>{b.service}</Label>
                      <Select value={form.service} onValueChange={set("service")}>
                        <SelectTrigger
                          id="service"
                          data-testid="booking-input-service"
                          className="mt-2 h-12 bg-white border-ink-900/12 text-ink-900 rounded-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                        >
                          <SelectValue placeholder={b.servicePh} />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-ink-900/10 text-ink-900">
                          {SERVICE_VALUES.map((val, i) => (
                            <SelectItem
                              key={val}
                              value={val}
                              data-testid={`booking-service-option-${val.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                              className="focus:bg-brand-50 focus:text-brand-700"
                            >
                              {b.options[i]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.service && <p className="text-xs text-red-600 mt-2">{errors.service}</p>}
                    </div>
                  </div>

                  <div className="mt-5">
                    <Label htmlFor="message" className={labelCls}>
                      {b.message} <span className="text-ink-500/60 normal-case tracking-normal font-normal">{b.optional}</span>
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="booking-input-message"
                      value={form.message}
                      onChange={set("message")}
                      rows={5}
                      placeholder={b.messagePh}
                      className="mt-2 bg-white border-ink-900/12 text-ink-900 rounded-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-none placeholder:text-ink-500/50"
                    />
                  </div>

                  <Button
                    data-testid="booking-form-submit"
                    type="submit"
                    disabled={loading}
                    className="mt-8 w-full sm:w-auto bg-brand-500 text-white hover:bg-brand-600 hover:-translate-y-0.5 transition-all duration-300 rounded-sm h-12 px-8 text-sm tracking-wide font-semibold disabled:opacity-70 shadow-sm"
                  >
                    {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{b.sending}</>) : b.submit}
                  </Button>
                  <p className="text-xs text-ink-500 mt-5 leading-relaxed">{b.consent}</p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, testId, value, onChange, error, type = "text", placeholder, labelCls, inputCls }) {
  return (
    <div>
      <Label htmlFor={id} className={labelCls}>{label}</Label>
      <Input id={id} data-testid={testId} type={type} value={value} onChange={onChange} placeholder={placeholder} className={inputCls} />
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
}
